/**
 * Playtest **sheet** view models — the half of the prototype's `ptVals()`
 * (`design/Games Tonight Mobile.dc.html` line 2036) that `selectors.ts` did not
 * already cover.
 *
 * `selectors.playtestsVM` ports the `pt` bag (the screen: groups, cards, chips,
 * the sync chip label, and thin `browse` / `tracked` projections). This file
 * ports the five sheet bags — `ptd` (2101), `pta` (2160), `ptb` (2178),
 * `ptt` (2192) and `ptsy` (2210) — under the same two rules `selectors.ts`
 * states in its header:
 *
 *   1. **No closures.** The prototype's `onClr` / `onAct` / `fpClick` /
 *      `tClick` become discriminators (`clrKind`, `actKind`) and plain URLs
 *      (`fpUrl`, `reqUrl`); the component binds the store action itself.
 *   2. All time-dependent selectors take an explicit `now`.
 *
 * Where the prototype lied, these read the real store instead (INVENTORY §12):
 *   - §12.1/§12.3 the sync chip and every "CHECKED …" string come from the
 *     freshness slice, never from a boolean;
 *   - §12.2 `PRIVATE — GOT-IN DETECTION OFF` is reachable — it is
 *     `steamLink.profilePublic === false`, a real failure state.
 */

import { chipOf, chkAgo, norm, ptFmt } from '../lib/format.ts';
import { PT_BROWSE, ptArt, ptBuckets, ptGroupOf, ptSquads } from '../lib/logic.ts';
import type { ArtCand, Playtest, PtBrowseRow, PtCheckResult, User } from '../lib/types.ts';
import { freshnessChip } from './selectors.ts';
import type { QdStore } from './types.ts';

const A = 'var(--acc)';
const A2 = 'var(--acc2)';
const AS = 'var(--accSoft)';

const ptDays = (ms: number) => Math.max(1, Math.round(Math.abs(ms) / 864e5));

// ---------------------------------------------------------------------------
// sync chip (the 2×2 grid's first cell)
// ---------------------------------------------------------------------------

export interface PtSyncChip {
  lbl: string;
  dot: string;
  bd: string;
  clr: string;
}

/**
 * INVENTORY §4.5 chip, with §12.3 fixed: the *text* is already the freshness
 * chip (`playtestsVM.syncLbl`), but the prototype's colours keyed off
 * `ptLinked` alone, so a failing revalidation still rendered as a calm grey
 * "LIVE ·". `retrying` / `OFFLINE` now paints `--acc2` like the unlinked state.
 */
export function ptSyncChip(s: QdStore, now: number = Date.now()): PtSyncChip {
  const chip = freshnessChip(s, now);
  const linked = !!(s.steamLink && s.steamLink.linked);
  const bad = !linked || s.freshness.state === 'retrying';
  return {
    lbl: chip.txt,
    dot: bad ? A2 : A,
    bd: bad ? A2 : 'var(--gt-border-2)',
    clr: bad ? A2 : 'var(--gt-muted)',
  };
}

// ---------------------------------------------------------------------------
// people rows (prototype `avRow`, line 2043 — `selectors.ts` keeps its copy private)
// ---------------------------------------------------------------------------

export interface PtPerson {
  key: string;
  k: string;
  i: string;
  bg: string;
  hasAv: boolean;
  avBg: string;
  name: string;
  meta: string;
  /** the `✕ CLEAR` affordance — mine only, and only where it means something */
  hasClr: boolean;
  /** which action clears it: interest toggle vs. self-reported status */
  clrKind: 'int' | 'st';
}

function people(
  e: Playtest,
  arr: readonly User[],
  kind: 'int' | 'st',
  meKey: string,
  isOff: boolean,
): PtPerson[] {
  return arr.map((u) => {
    const c = chipOf(u);
    const st = e.st[u.k];
    return {
      key: u.k,
      k: u.k,
      i: c.ini,
      bg: c.hueBg,
      hasAv: c.hasAv,
      avBg: c.avBg,
      name: (u.name + (meKey && u.k === meKey ? ' — YOU' : '')).toUpperCase(),
      meta: kind === 'int' ? 'MARKED ' + ptFmt(e.int[u.k] ?? 0) : (st ? st.m : '').toUpperCase(),
      // Prototype line 2115: a Steam-detected status is not mine to clear.
      //
      // `req` is the exception, and it is an exception about the BACKEND, not
      // about this row: `steam-sync` only ever writes `status:'in'` (there is
      // no Steam signal for a pending request — see the REQUESTED box below),
      // so every `req` in the store came from the self-report button and its
      // author must be able to take it back. Without this clause a member who
      // mis-tapped `+ I SIGNED UP` on a Steam playtest would be stuck in the
      // REQUESTED list until a grant overwrote them.
      hasClr: !!(meKey && u.k === meKey && (kind === 'int' || isOff || (st && st.s === 'req'))),
      clrKind: kind,
    };
  });
}

// ---------------------------------------------------------------------------
// 4 — playtest detail sheet (prototype 990-1069 / ptd 2101)
// ---------------------------------------------------------------------------

export interface PtDetailBox {
  key: string;
  h: string;
  hc: string;
  tri: boolean;
  rows: PtPerson[];
  none: boolean;
  hasAct: boolean;
  act: string;
  /** `ptReport(id, actKind)` — `''` when `hasAct` is false */
  actKind: '' | 'in' | 'req';
}

export interface PtDetailVM {
  id: string;
  headKind: string;
  headNote: string;
  heroBg: string;
  ini: string;
  /** the hero art ladder — `components/PtArt.tsx#PtHeroArt` walks it */
  cands: ArtCand[];
  name: string;
  sub: string;
  isOff: boolean;
  srcChip: string;
  bnBd: string;
  bnBg: string;
  bnLive: boolean;
  bnClr: string;
  bnTxt: string;
  bnSub: string;
  sqs: Array<{ key: string; bg: string; bd: string; ml: string }>;
  sqLbl: string;
  byIni: string;
  byBg: string;
  byHasAv: boolean;
  byAvBg: string;
  attrTxt: string;
  boxes: PtDetailBox[];
  selfNote: string;
  desc: string;
  hasLink: boolean;
  linkHref: string;
  linkTxt: string;
  note1: string;
  note2: string;
  hasFp: boolean;
  fpTxt: string;
  fpUrl: string;
  /** the primary is a SIGNUP hand-off → raise `openPtAsk(id)` alongside the tab */
  fpAsk: boolean;
  fiTxt: string;
  fiBd: string;
  fiClr: string;
  fiBg: string;
  /** true → UNTRACK is a per-user mute and stays one tap (PORTING-NOTES D-04). */
  auto: boolean;
  /** `SURE?` while armed, otherwise `UNTRACK` — the Crew REMOVE `rmArm` wording. */
  unTxt: string;
  /** armed flips the border to `--acc2`, like Crew's armed `rmBd`. */
  unBd: string;
  /** armed flips the label to `--acc2`, like Crew's armed `rmClr`. */
  unClr: string;
}

export function ptDetailVM(s: QdStore, now: number = Date.now()): PtDetailVM | null {
  const e = s.ptDet ? s.pts.find((x) => x.id === s.ptDet) : null;
  if (!e) return null;

  const meKey = s.session;
  const a = ptArt(e, s.assets);
  const g = ptGroupOf(e, now);
  const b = ptBuckets(e, s.users);
  const mine = meKey ? e.st[meKey] : undefined;
  const m = mine ? mine.s : '';
  const on = !!(meKey && e.int[meKey]);
  const isOff = e.kind === 'off';
  const rng = ptFmt(e.wStart) + ' → ' + ptFmt(e.wEnd);
  // Same guard as `ptTrackedVM`: only the crew-wide UNTRACK arms. An `auto`
  // playtest is a per-user mute (PORTING-NOTES D-04) and must NEVER read
  // `SURE?`, even if a stale id is sitting in `ptRmArm`.
  const armed = !e.auto && s.ptRmArm === e.id;

  const bn =
    g === 'live'
      ? { bnBd: A, bnBg: AS, bnLive: true, bnClr: A, bnTxt: 'LIVE NOW · ' + rng, bnSub: ptDays(e.wEnd - now) + ' DAYS LEFT' }
      : g === 'up'
        ? { bnBd: 'var(--gt-border-2)', bnBg: 'var(--gt-panel)', bnLive: false, bnClr: 'var(--gt-text)', bnTxt: 'UPCOMING · ' + rng, bnSub: 'IN ' + ptDays(e.wStart - now) + ' DAYS' }
        : g === 'ended'
          ? { bnBd: 'var(--gt-border-2)', bnBg: 'var(--gt-panel)', bnLive: false, bnClr: 'var(--gt-muted)', bnTxt: 'ENDED' + (e.wEnd ? ' · ' + ptFmt(e.wEnd) : ''), bnSub: 'ARCHIVED' }
          : {
              bnBd: 'var(--gt-border-2)',
              bnBg: 'var(--gt-panel)',
              bnLive: false,
              bnClr: 'var(--gt-text)',
              bnTxt: e.status === 'open' ? 'OPEN SIGNUPS — NO DATED WINDOW' : 'NO DATED WINDOW',
              bnSub: e.foot,
            };

  const byC = chipOf(s.user(e.by));

  // §12.3: the header note is a freshness claim, so it has to come from the
  // freshness slice. The §9 wording survives verbatim on the happy path; a
  // stale or unlinked account says so instead of asserting "LIVE".
  const fresh = freshnessChip(s, now);
  const linkedOk = !!(s.steamLink && s.steamLink.linked) && s.freshness.state === 'ok';
  const headNote = isOff
    ? 'USER-MAINTAINED — NO AUTO-SYNC'
    : linkedOk
      ? 'TRACKED · LIVE — CHECKED ' + chkAgo(s.freshness.lastOkAt, now)
      : 'TRACKED · ' + fresh.txt;

  // `fpAsk` marks the SIGNUP HAND-OFF specifically, never INSTALL: leaving for
  // the store page is the one moment the app knows a request may have happened,
  // so it is the moment the confirm is raised (see `ui.ts#ptAsk`). INSTALL is a
  // member who is already `in` — there is nothing left to ask them.
  let fp = { hasFp: false, fpTxt: '', fpUrl: '', fpAsk: false };
  if (m === 'in' && e.kind === 'steam' && e.url) fp = { hasFp: true, fpTxt: 'INSTALL', fpUrl: e.url, fpAsk: false };
  else if (m !== 'in' && e.status === 'open' && e.url && g !== 'ended')
    fp = {
      hasFp: true,
      fpTxt: isOff ? 'SIGN UP ON ' + (e.srcLabel || 'SITE') : 'REQUEST ACCESS',
      fpUrl: e.url,
      fpAsk: true,
    };

  return {
    id: e.id,
    headKind: '◆ PLAYTEST' + (isOff ? ' · OFF-STEAM' : ''),
    headNote,
    heroBg: a.fall,
    ini: a.ini,
    cands: a.cands,
    name: e.name.toUpperCase(),
    sub: (e.dev + ' · ' + e.party.lbl + ' · ' + (g === 'ended' ? 'TEST ENDED' : 'UNRELEASED')).toUpperCase(),
    isOff,
    srcChip: e.srcLabel || 'OFF-STEAM',
    ...bn,
    sqs: ptSquads(e),
    sqLbl: e.party.lbl,
    byIni: byC.ini,
    byBg: byC.hueBg,
    byHasAv: byC.hasAv,
    byAvBg: byC.avBg,
    attrTxt:
      'ADDED BY ' +
      byC.name.toUpperCase() +
      ' · ' +
      ptFmt(e.at) +
      " — OFF-STEAM ENTRIES DON'T AUTO-SYNC. STATUS + DATES ARE USER-EDITED; EVERY EDIT IS ATTRIBUTED. " +
      e.datesSrc,
    boxes: [
      {
        key: 'i',
        h: 'INTERESTED (' + b.int.length + ')',
        hc: A,
        tri: true,
        rows: people(e, b.int, 'int', meKey, isOff),
        none: !b.int.length,
        hasAct: false,
        act: '',
        actKind: '',
      },
      {
        key: 'r',
        h: '⋯ REQUESTED (' + b.req.length + ')',
        hc: 'var(--gt-text-3)',
        tri: false,
        rows: people(e, b.req, 'st', meKey, isOff),
        none: !b.req.length,
        // NOT gated on `isOff`, unlike HAS ACCESS below. Steam's Web API
        // exposes a library, not a queue: `GetOwnedGames` can tell us a member
        // was GRANTED access (an appid appears) and can never tell us they
        // ASKED for it, because requesting adds nothing until the dev grants.
        // So on a Steam row this button is not a fallback for detection that
        // failed — it is the only way this rung of the ladder is ever filled,
        // which is what `PT_DATA_FOOTER`'s "REQUESTS SELF-REPORTED" has always
        // claimed. It was gated for two releases, and the REQUESTED list on
        // every Steam playtest was permanently empty as a result.
        hasAct: !!(meKey && m !== 'req' && m !== 'in'),
        act: '+ I SIGNED UP',
        actKind: 'req',
      },
      {
        key: 'a',
        h: '✓ HAS ACCESS (' + b.inn.length + ')',
        hc: A,
        tri: false,
        rows: people(e, b.inn, 'st', meKey, isOff),
        none: !b.inn.length,
        hasAct: !!(isOff && meKey && m !== 'in'),
        act: '+ I GOT IN',
        actKind: 'in',
      },
    ],
    // `(SKIPPING STEPS IS FINE)` is load-bearing rather than chatty: the two
    // lists are a ladder (requested → got in) and a member who was invited
    // straight into a wave never touched the first rung. Without the clause the
    // ladder reads as a required sequence, and the honest answer — report only
    // what happened to you — is exactly what the buttons allow.
    //
    // TWO NOTES, because the two kinds of row are not in the same situation and
    // one string cannot describe both. Off-Steam syncs nothing, so both rungs
    // are hand-written. A Steam row's SECOND rung is detected from the member's
    // library by `steam-sync`; only the first is self-reported, and saying
    // "STEAM DOESN'T SYNC" there would contradict the tracked header two
    // elements above it, which reads `TRACKED · LIVE — CHECKED <N>S AGO`.
    selfNote: isOff
      ? 'SELF-REPORTED — ' +
        (e.srcLabel || 'THIS SITE') +
        " DOESN'T SYNC · REPORT WITH THE BUTTONS IN EACH LIST (SKIPPING STEPS IS FINE) · UPDATES POST TO WHAT'S NEW"
      : 'REQUESTS ARE SELF-REPORTED — STEAM PUBLISHES LIBRARIES, NOT REQUEST QUEUES · ' +
        '✓ HAS ACCESS IS DETECTED FROM YOURS (SKIPPING STEPS IS FINE)',
    desc: e.desc || e.note,
    hasLink: !!e.url,
    linkHref: e.url || '#',
    linkTxt: isOff ? (e.srcLabel || 'SIGNUP') + ' ↗' : 'STEAM STORE PAGE ↗',
    note1: isOff
      ? 'LINK VERIFIED OFFICIAL BY DOMAIN (' + (e.srcLabel || '—') + ')'
      : 'OFFICIAL LINKS ONLY — “PLAYTEST INVITES” SENT OVER CHAT ARE A KNOWN SCAM',
    note2: isOff ? 'CHAT-DM “INVITES” ARE A KNOWN SCAM — NEVER TRUST THEM' : e.datesSrc,
    ...fp,
    fiTxt: on ? 'INTERESTED' : 'MARK INTEREST',
    fiBd: on ? A : 'var(--gt-border-3)',
    fiClr: on ? A : 'var(--gt-text-3)',
    fiBg: on ? AS : 'transparent',
    auto: e.auto,
    unTxt: armed ? 'SURE?' : 'UNTRACK',
    unBd: armed ? A2 : 'var(--gt-border-3)',
    unClr: armed ? A2 : 'var(--gt-text-5)',
  };
}

// ---------------------------------------------------------------------------
// 4b — "DID YOU REQUEST ACCESS?" (no prototype ancestor — see below)
// ---------------------------------------------------------------------------

/**
 * NOT a port. The prototype has no such surface because the prototype claimed
 * both rungs were detected (INVENTORY §12.5 records that neither ever was).
 *
 * The real constraint is upstream of the app: requesting playtest access on
 * Steam creates no artefact any third-party key can read. `GetOwnedGames`
 * answers with a library, and a pending request is not in it — which is why
 * `functions/steam-sync/index.ts` writes `status:'in'` and has no `'req'` arm
 * to fix. The nightly store-widget scrape is no help either: it sees the
 * widget, not who pressed it.
 *
 * So the click is the signal. The member is leaving for the store page RIGHT
 * NOW with the stated intention of asking, and when they come back this is
 * sitting over the card waiting for a yes or a no. What it must not do is
 * ASSUME the yes — a member who opens the tab, reads the requirements and
 * closes it has requested nothing, and a REQUESTED list that quietly includes
 * them is worse than the empty one this replaces.
 *
 * The card is repeated in full (art, name, dev, chips) rather than trusted to
 * memory: on the phone the store page opens in a new tab and the answer can
 * arrive minutes later, in a different app, with three playtests open.
 */
export interface PtAskVM {
  id: string;
  /** header question — the sheet/modal title */
  title: string;
  /** the hand-off that raised this, e.g. `STEAM STORE PAGE` */
  srcTxt: string;
  heroBg: string;
  ini: string;
  /** the hero art ladder — `components/PtArt.tsx#PtHeroArt` walks it */
  cands: ArtCand[];
  name: string;
  dev: string;
  chips: Array<{ key: string; t: string }>;
  /** the question body */
  body: string;
  /** the honest reason we have to ask at all */
  why: string;
  yesTxt: string;
  noTxt: string;
  /** re-open the signup page — the member may have lost the tab */
  hasLink: boolean;
  linkTxt: string;
  linkHref: string;
}

export function ptAskVM(s: QdStore): PtAskVM | null {
  const e = s.ptAsk ? s.pts.find((x) => x.id === s.ptAsk) : null;
  if (!e) return null;

  const a = ptArt(e, s.assets);
  const isOff = e.kind === 'off';
  const src = isOff ? e.srcLabel || 'THE SIGNUP PAGE' : 'STEAM STORE PAGE';

  return {
    id: e.id,
    title: 'DID YOU REQUEST ACCESS?',
    srcTxt: src,
    heroBg: a.fall,
    ini: a.ini,
    cands: a.cands,
    name: e.name.toUpperCase(),
    dev: e.dev.toUpperCase(),
    chips: [
      { key: 'c1', t: isOff ? 'OFF-STEAM · ' + (e.srcLabel || '—') : 'STEAM' },
      { key: 'c2', t: e.party.lbl },
    ],
    body:
      'ONLY IF YOU ACTUALLY SENT THE REQUEST ON ' +
      src +
      '. YES PUTS YOU IN THE ⋯ REQUESTED LIST FOR THE PARTY; YOU CAN CLEAR IT ANY TIME.',
    // Answering this is not busywork and the copy has to say why, or it reads
    // as a dialog the app could have skipped.
    why: isOff
      ? (e.srcLabel || 'THIS SITE') + " DOESN'T SYNC WITH US, SO NOBODY SEES IT UNLESS YOU SAY SO."
      : "STEAM DOESN'T PUBLISH WHO ASKED — ONLY WHO WAS GRANTED. WE'LL SPOT THE GRANT ON ITS OWN.",
    yesTxt: '✓ YES — I REQUESTED IT',
    noTxt: 'NOT YET',
    hasLink: !!e.url,
    linkTxt: 'REOPEN ' + src + ' ↗',
    linkHref: e.url || '#',
  };
}

// ---------------------------------------------------------------------------
// 5 — add playtest sheet (prototype 1071-1145 / pta 2160)
// ---------------------------------------------------------------------------

export interface PtPartyChip {
  key: string;
  n: number;
  l: string;
  bd: string;
  bg: string;
  clr: string;
}

export interface PtAddVM {
  isSteam: boolean;
  isOff: boolean;
  tabSClr: string;
  tabSBd: string;
  tabOClr: string;
  tabOBd: string;
  url: string;
  /**
   * The CHECK round trip is in flight. Same treatment as P3e-03's sync sheet
   * (`syncLbl`/`busy`): the label carries the state, the panel branches do not.
   */
  checking: boolean;
  checkLbl: string;
  /**
   * The TRACK write is in flight — the same treatment one step further on.
   * Shared by both tabs: the off-Steam arm is the one with no unique index
   * behind it, so this flag is the only thing standing between a double-tap
   * and two identical crew-wide playtests.
   */
  tracking: boolean;
  trackLbl: string;
  hasOk: boolean;
  hasErr: boolean;
  err: string;
  name: string;
  dev: string;
  sub: string;
  /**
   * P6 F-2: the panel headline. `✓ REQUEST-ACCESS WIDGET FOUND` (INVENTORY §9,
   * verbatim) is now conditional on a widget really being on the page —
   * INVENTORY §12 item 7 called out that it was unconditional. `okClr`/`okBd`
   * carry the accent so a no-widget answer does not paint itself green.
   */
  okTitle: string;
  okClr: string;
  okBd: string;
  okBg: string;
  /** the art ladder — `components/PtArt.tsx#PtCover` walks it */
  cands: ArtCand[];
  /** the checked appid, `0` before a check — the probe's cache key */
  appid: number;
  fall: string;
  ini: string;
  party: PtPartyChip[];
  offName: string;
  offUrl: string;
  offStart: string;
  offEnd: string;
}

/** `[1,2,3,4,5,6]` → `SOLO 2 3 4 5 6+` (prototype line 2170). */
export function ptPartyChips(sel: number): PtPartyChip[] {
  return [1, 2, 3, 4, 5, 6].map((n) => {
    const on = sel === n;
    return {
      key: '' + n,
      n,
      l: n === 1 ? 'SOLO' : n === 6 ? '6+' : '' + n,
      bd: on ? A : 'var(--gt-border-2)',
      bg: on ? A : 'transparent',
      clr: on ? 'var(--gt-ink)' : 'var(--gt-text-5)',
    };
  });
}

type PtCheckOk = Extract<PtCheckResult, { ok: true }>;

const okChk = (c: PtCheckResult | null): PtCheckOk | null => (c && c.ok ? c : null);

export function ptAddVM(s: QdStore): PtAddVM {
  const chk = s.ptAddChk;
  const ok = okChk(chk);
  const art = ok
    ? ptArt({ appid: ok.appid, artUrl: '', hue: ok.hue, name: ok.name }, s.assets)
    : { cands: [], fall: '', ini: '' };
  const isSteam = s.ptAddTab === 'steam';
  return {
    isSteam,
    isOff: !isSteam,
    tabSClr: isSteam ? A : 'var(--gt-muted)',
    tabSBd: isSteam ? A : 'transparent',
    tabOClr: isSteam ? 'var(--gt-muted)' : A,
    tabOBd: isSteam ? 'transparent' : A,
    url: s.ptAddUrl,
    checking: s.ptAddChecking,
    checkLbl: s.ptAddChecking ? 'CHECKING…' : 'CHECK THE STORE PAGE',
    /* The write, as opposed to the lookup above. One label for both tabs —
       they share the panel and the flag. */
    tracking: s.ptTracking,
    trackLbl: s.ptTracking ? 'TRACKING…' : '+ TRACK FOR EVERYONE',
    hasOk: !!ok,
    hasErr: !!(chk && !chk.ok),
    err: chk && !chk.ok ? chk.err : '',
    name: ok ? ok.name : '',
    dev: ok ? ok.dev.toUpperCase() : '',
    sub: ok ? ok.sub : '',
    okTitle: ok && ok.widget ? '✓ REQUEST-ACCESS WIDGET FOUND' : '— NO REQUEST-ACCESS WIDGET',
    okClr: ok && ok.widget ? A : 'var(--gt-text-5)',
    okBd: ok && ok.widget ? A : 'var(--gt-border-2)',
    okBg: ok && ok.widget ? 'var(--accSoft)' : 'transparent',
    cands: art.cands,
    appid: ok ? ok.appid : 0,
    fall: art.fall,
    ini: art.ini,
    party: ptPartyChips(s.ptAddParty),
    offName: s.ptOffName,
    offUrl: s.ptOffUrl,
    offStart: s.ptOffStart,
    offEnd: s.ptOffEnd,
  };
}

// ---------------------------------------------------------------------------
// 6 — browse Steam playtests sheet (prototype 1147-1187 / ptb 2178)
// ---------------------------------------------------------------------------

export interface PtBrowseRowVM {
  key: string;
  /** the raw catalog row, for `ptFromBrowse` (which needs the un-cased dev) */
  row: PtBrowseRow;
  appid: number;
  name: string;
  dev: string;
  meta: string;
  /** the art ladder — `components/PtArt.tsx#PtCover` walks it */
  cands: ArtCand[];
  fall: string;
  ini: string;
  chips: Array<{ key: string; t: string; bd: string; c: string; bg: string }>;
  tracked: boolean;
  tTxt: string;
  tBd: string;
  tClr: string;
  tBg: string;
  hasReq: boolean;
  reqUrl: string;
}

export interface PtBrowseVM {
  q: string;
  rows: PtBrowseRowVM[];
  head: string;
  foot: string;
}

/** INVENTORY §9 — `2,143` here and `212` in the footer disagree in the frozen
 * copy; both stay verbatim (INVENTORY §12 item 8). The tail deliberately
 * diverges: no catalog mode, no `playtest_catalog` table (ARCHITECTURE §3.4). */
export const PT_BROWSE_HEAD =
  '2,143 UNRELEASED GAMES WITH A REQUEST-ACCESS WIDGET · SAMPLE ROWS — REAL CATALOG NOT WIRED';

export function ptBrowseVM(s: QdStore): PtBrowseVM {
  const bq = norm(s.ptBq);
  const rows = PT_BROWSE.filter((x) => !bq || norm(x.name).includes(bq) || norm(x.dev).includes(bq)).map((x) => {
    const tracked = s.pts.some((e) => (x.appid && e.appid === x.appid) || norm(e.name) === norm(x.name));
    const a = ptArt({ appid: x.appid, artUrl: '', hue: (x.name.length * 47) % 360, name: x.name }, s.assets);
    return {
      key: x.name,
      row: { ...x, tags: [...x.tags] },
      appid: x.appid,
      name: x.name,
      dev: x.dev.toUpperCase(),
      meta: x.meta,
      cands: a.cands,
      fall: a.fall,
      ini: a.ini,
      chips: x.tags.map((t) =>
        t === 'OPEN SIGNUPS'
          ? { key: t, t, bd: A, c: A, bg: AS }
          : t === 'INVITE ONLY'
            ? { key: t, t, bd: A2, c: A2, bg: 'transparent' }
            : { key: t, t, bd: 'var(--gt-border-2)', c: 'var(--gt-text-5)', bg: 'transparent' },
      ),
      tracked,
      tTxt: tracked ? 'TRACKING ✓' : '☆ TRACK',
      tBd: tracked ? 'var(--gt-border-3)' : 'var(--gt-text)',
      tClr: tracked ? 'var(--gt-text-5)' : 'var(--gt-text)',
      tBg: 'transparent',
      hasReq: x.open,
      reqUrl: x.appid
        ? 'https://store.steampowered.com/app/' + x.appid + '/'
        : 'https://store.steampowered.com/search/?term=' + encodeURIComponent(x.name),
    };
  });
  return {
    q: s.ptBq,
    rows,
    head: PT_BROWSE_HEAD,
    // Middle clause deliberately diverges from the prototype: no Steam
    // “playtests”-type search exists and no catalog was ever built (P6 §4,
    // ARCHITECTURE §3.4). The `212` stays verbatim (INVENTORY §12 item 8).
    foot:
      rows.length +
      " OF 212 MATCHES SHOWN · SAMPLE ROWS — REAL CATALOG NOT WIRED · TRACKING ASKS FOR PARTY SIZE, THEN ADDS IT TO EVERYONE'S PLAYTESTS TAB",
  };
}

// ---------------------------------------------------------------------------
// 7 — tracked sheet (prototype 1189-1213 / ptt 2192)
// ---------------------------------------------------------------------------

export interface PtTrackedRow {
  key: string;
  id: string;
  n: string;
  st: string;
  stc: string;
  dot: string;
  dim: string;
  src: string;
  /**
   * true → `ptUntrack` mutes for me only and toasts `MUTED FOR YOU`, so the
   * `✕` stays a single tap. false → it removes the row crew-wide, which is the
   * tap the sheet now arms first (`xTxt`/`xClr`/`xFs` below).
   */
  auto: boolean;
  /** `SURE?` while armed, otherwise `✕` — the Crew REMOVE `rmArm` wording. */
  xTxt: string;
  /** armed flips to `--acc2`, the same alarm colour Crew's armed REMOVE takes. */
  xClr: string;
  /** `SURE?` has to fit the 44px tap target; 8.5px is the sheet's mono size. */
  xFs: string;
}

export interface PtTrackedVM {
  n: string;
  rows: PtTrackedRow[];
}

/**
 * `playtestsVM.tracked` already ships `n / st / dim / src`; this adds the
 * status colour (`stc`) and the 8px dot the sheet paints with it, which the
 * screen bag never needed — plus the `✕` cell's arm state, which only exists
 * in the sheet.
 */
export function ptTrackedVM(s: QdStore, now: number = Date.now()): PtTrackedVM {
  const meKey = s.session;
  return {
    n: '' + s.pts.length,
    rows: s.pts.map((e) => {
      const g = ptGroupOf(e, now);
      const mine = meKey ? e.st[meKey] : undefined;
      const m = mine ? mine.s : '';
      const st =
        (m === 'in'
          ? 'IN'
          : m === 'req'
            ? 'REQUESTED'
            : e.status === 'none'
              ? 'NO PUBLIC SIGNUP'
              : g === 'ended'
                ? 'ENDED'
                : 'OPEN') +
        (g === 'live' ? ' · LIVE NOW' : '') +
        (e.kind === 'off' ? ' · OFF-STEAM' : '');
      const stc =
        m === 'in' ? A : g === 'ended' ? 'var(--gt-muted)' : e.status === 'none' ? A2 : 'var(--gt-text-3)';
      const byU = s.user(e.by);
      // Only the crew-wide `✕` arms. An `auto` row is a per-user mute
      // (PORTING-NOTES D-04) and must NEVER read `SURE?`, even if a stale id
      // is sitting in `ptRmArm`.
      const armed = !e.auto && s.ptRmArm === e.id;
      return {
        key: e.id,
        id: e.id,
        n: e.name,
        st,
        stc,
        dot: stc,
        dim: g === 'ended' ? '.55' : '1',
        src: e.auto
          ? 'AUTO — GAMES TONIGHT?'
          : meKey && e.by === meKey
            ? 'YOU'
            : byU
              ? 'ADDED BY ' + byU.name.toUpperCase()
              : '—',
        auto: e.auto,
        xTxt: armed ? 'SURE?' : '✕',
        xClr: armed ? A2 : 'var(--gt-muted)',
        xFs: armed ? '8.5px' : '13px',
      };
    }),
  };
}

/**
 * The Tracked sheet's footnote — the one place the app states what auto-track
 * actually does, so it has to move whenever the trigger does.
 *
 * It said `AUTO-TRACK: UNRELEASED GAMES ON GAMES TONIGHT?`, which described the
 * old rule exactly: every unreleased game on the list got a tile, whether or not
 * it had a playtest. Migration 3005 made the store-page widget the test, so an
 * unreleased game is now WATCHED rather than tracked, and the tile arrives only
 * if a signup page does. `TRACKS` → `WATCHES` on the interest clause is the same
 * correction: calling S or A starts a watch, it does not assert a playtest.
 */
export const PT_TRACKED_FOOT =
  'AUTO-TRACK: UNRELEASED GAMES ON GAMES TONIGHT? ARE WATCHED — THEY APPEAR HERE WHEN A SIGNUP PAGE DOES · CALLING S/A INTEREST ALSO WATCHES · REMOVING AN AUTO ENTRY MUTES IT FOR YOU ONLY';

// ---------------------------------------------------------------------------
// 8 — steam account / sync sheet (prototype 1215-1258 / ptsy 2210)
// ---------------------------------------------------------------------------

export interface PtSyncVM {
  linked: boolean;
  notLinked: boolean;
  acctName: string;
  acctIni: string;
  acctBg: string;
  s1Bd: string;
  s1Num: string;
  s1Sub: string;
  s2Bd: string;
  s2Num: string;
  s2Chip: string;
  s2ChipBd: string;
  s2ChipClr: string;
  s2ChipBg: string;
  /** the `CHECKED <NS AGO>` in the footer — freshness, not theatre (§12.3) */
  lastSync: string;
  syncLbl: string;
  busy: boolean;
  foot: string;
}

/**
 * The sync sheet's header note.
 *
 * The mirror reads `POWERS “GOT IN” + “REQUESTED”`, and the second half was
 * never true of this sheet. `steam-sync ?op=self` calls GetOwnedGames: Steam
 * publishes what a member OWNS, and a pending playtest request is not an
 * ownership fact, so no amount of pressing CHECK NOW can fill the ⋯ REQUESTED
 * list. It is filled by `+ I SIGNED UP` on the playtest itself.
 *
 * Same class of correction as the three INVENTORY §12 items in this file's
 * header — copy that promised detection the backend cannot perform. This one
 * had a cost: it is why CHECK NOW was pressed repeatedly against two open
 * playtests, waiting for a status change that was never coming.
 *
 * A constant, not a literal in the sheet, so the phone and desktop notes cannot
 * drift apart again (`selectors.d4#PT_SYNC_NOTE_DESKTOP` is the longer twin).
 */
export const PT_SYNC_NOTE = 'POWERS “GOT IN” — REQUESTS ARE SELF-REPORTED';

export const PT_SYNC_STEP2 =
  'THE STEP EVERYONE MISSES. WE READ YOUR LIBRARY WHEN YOU PRESS CHECK NOW, AND ONCE A NIGHT AS A BACKSTOP — A GRANTED PLAYTEST SHOWS UP AS AN OWNED APP → “GOT IN”. GAME DETAILS MUST BE PUBLIC OR WE CANNOT SEE IT. PROFILE ▸ PRIVACY ▸ GAME DETAILS ▸ PUBLIC.';

/**
 * Steam attribution + the plain-language privacy note — the compliance block.
 *
 * This app has no settings screen, no about page and no legal surface of any
 * kind; the sync sheet is the ONE place a member ever looks at their Steam
 * link. So the attribution Valve's terms require AND the disclosure of what we
 * hold about them live here, on both trees (`sheets/PtSyncSheet.tsx`,
 * `desktop/modals/PtSyncModal.tsx`).
 *
 * Every clause is a claim the backend makes true, not a reassurance:
 *   - the stored set is exactly the columns of `steam_links` +
 *     `steam_owned_apps` (migration `20260808_0800_steam.sql`) — id, persona,
 *     avatar, and the owned appid/playtime rows. Nothing else is written, and
 *     the password never reaches us because sign-in is Steam's own OpenID page;
 *   - the region is wherever the Supabase project runs (us-east-1);
 *   - `steam-link ?op=unlink` deletes the link row, the owned-apps cache and
 *     every Steam-sourced playtest status — so "UNLINK DELETES ALL OF IT" is
 *     the endpoint's actual behaviour.
 *
 * The link is rendered `rel="noreferrer"` and deliberately NOT `nofollow`:
 * Valve's terms require the attribution link to be followable.
 */
export const PT_SYNC_STEAM_HREF = 'https://store.steampowered.com';

/** The anchor text. `↗` is the house marker for "opens a new tab". */
export const PT_SYNC_ATTR_LINK = 'POWERED BY STEAM ↗';

export const PT_SYNC_ATTR_TAIL =
  ' · STEAM AND THE STEAM LOGO ARE TRADEMARKS OF VALVE CORPORATION · QUARTERDOWN IS NOT AFFILIATED WITH VALVE';

export const PT_SYNC_PRIVACY =
  "WE STORE: YOUR STEAM ID · PERSONA NAME + AVATAR · OWNED APPIDS + PLAYTIME. NOTHING ELSE — WE NEVER GET YOUR PASSWORD, SIGN-IN IS STEAM'S OWN PAGE. HELD IN SUPABASE (UNITED STATES · US-EAST-1). UNLINK DELETES ALL OF IT.";

export function ptSyncVM(s: QdStore, now: number = Date.now()): PtSyncVM {
  const link = s.steamLink;
  const linked = !!(link && link.linked);
  // §12.2: `profilePublic` is a real server-read fact, so PRIVATE is reachable.
  const pub = !!(link && link.profilePublic);
  const meU = s.me();
  const f = s.freshness;
  // Prototype `this._chkAt || Date.now()`: an unchecked account reads JUST NOW.
  // The moment a real round-trip lands, `lastOkAt` takes over for good.
  const lastSync = chkAgo(f.lastOkAt ?? (link ? link.lastSyncAt : null), now);

  return {
    linked,
    notLinked: !linked,
    acctName:
      link && link.personaName
        ? link.personaName
        : ((meU ? meU.name : 'PLAYER').toUpperCase().replace(/[^A-Z0-9]/g, '') + '_PLAYS'),
    acctIni: ((meU ? meU.name : 'P')[0] ?? 'P').toUpperCase(),
    acctBg: meU ? 'oklch(0.55 0.12 ' + meU.hue + ')' : 'var(--gt-border-3)',
    s1Bd: linked ? A : A2,
    s1Num: linked ? A : A2,
    s1Sub: linked
      ? "LINKED — STEAM'S OWN SIGN-IN PAGE, WE ONLY GET YOUR ID."
      : "OPENS STEAM'S OWN SIGN-IN PAGE. WE GET YOUR STEAMID — NEVER YOUR PASSWORD.",
    s2Bd: linked ? (pub ? A : A2) : 'var(--gt-line)',
    s2Num: linked ? (pub ? A : A2) : 'var(--gt-muted-2)',
    s2Chip: !linked ? 'AFTER STEP 1' : pub ? 'PUBLIC ✓' : 'PRIVATE — GOT-IN DETECTION OFF',
    s2ChipBd: linked && !pub ? A2 : 'var(--gt-border-2)',
    s2ChipClr: linked ? (pub ? A : A2) : 'var(--gt-muted-2)',
    s2ChipBg: 'transparent',
    lastSync,
    syncLbl: f.state === 'busy' ? 'CHECKING…' : 'CHECK NOW',
    busy: f.state === 'busy',
    foot:
      'CHECKED ' +
      lastSync +
      " · UPDATES ON CHECK NOW · NIGHTLY SWEEP ~09:17 UTC AS BACKSTOP · WE NEVER GET YOUR PASSWORD · OFF-STEAM TESTS AREN'T COVERED — SELF-REPORT ON THE GAME",
  };
}
